import React, { useEffect, useMemo, useState } from "react";
import { JobApplication } from "./ApplicationWizard";
import { servicePlansService } from "../services/servicePlansService";
import {
  GuidedInterviewPrepMode,
  GuidedInterviewPrepRequest,
  GuidedInterviewPrepTopicSelection,
  GuidedInterviewPrepType,
} from "../services/jobApplicationService";
import {
  PREP_MODE_OPTIONS,
  PREP_TYPE_OPTIONS,
  getSuggestedTopics,
} from "./interviewPrepOptions";
import "./InterviewPrepWizard.css";

interface InterviewPrepWizardProps {
  application: JobApplication;
  initialValues?: Partial<GuidedInterviewPrepRequest>;
  isSubmitting?: boolean;
  onComplete: (request: GuidedInterviewPrepRequest) => void;
  onCancel: () => void;
}

type WizardStep = "topics" | "mode" | "context" | "review";

const DEFAULT_TOPIC_COUNT = 3;
const MAX_TOPIC_COUNT = 10;

const clampTopicCount = (value: number): number =>
  Math.min(MAX_TOPIC_COUNT, Math.max(1, value));

const normalizeTopicSelection = (
  selection: Partial<GuidedInterviewPrepTopicSelection> | string,
): GuidedInterviewPrepTopicSelection | null => {
  const topic =
    typeof selection === "string"
      ? selection.trim()
      : String(selection.topic || "").trim();

  if (!topic) {
    return null;
  }

  const label =
    typeof selection === "string"
      ? topic
      : String(selection.label || "").trim() || topic;

  const description =
    typeof selection === "string"
      ? undefined
      : String(selection.description || "").trim() || undefined;

  return {
    topic,
    question_count: clampTopicCount(
      typeof selection === "string"
        ? DEFAULT_TOPIC_COUNT
        : Number(selection.question_count ?? DEFAULT_TOPIC_COUNT),
    ),
    label,
    description,
  };
};

const buildTopicSelections = (
  initialValues?: Partial<GuidedInterviewPrepRequest>,
): GuidedInterviewPrepTopicSelection[] => {
  const selectedTopics = initialValues?.selected_topics
    ?.map((topic) => normalizeTopicSelection(topic))
    .filter((topic): topic is GuidedInterviewPrepTopicSelection => Boolean(topic));

  if (selectedTopics && selectedTopics.length > 0) {
    return selectedTopics;
  }

  const legacyFocusAreas = initialValues?.focus_areas
    ?.map((focusArea) => normalizeTopicSelection(focusArea))
    .filter((topic): topic is GuidedInterviewPrepTopicSelection => Boolean(topic));

  return legacyFocusAreas || [];
};

const buildInitialRequest = (
  application: JobApplication,
  initialValues?: Partial<GuidedInterviewPrepRequest>,
): GuidedInterviewPrepRequest => {
  const selectedTopics = buildTopicSelections(initialValues);
  const liveResearch = Boolean(
    initialValues?.use_live_research ?? initialValues?.live_research,
  );

  return {
    prep_id: initialValues?.prep_id,
    job_application_id: application.id || "",
    company: application.company || "",
    position: application.position || "",
    job_description: application.jobSummary || application.description || "",
    position_core: application.positionCore || "",
    prep_type: initialValues?.prep_type || "behavioral",
    output_mode: initialValues?.output_mode || "model_answers",
    selected_topics: selectedTopics,
    live_research: liveResearch,
    use_live_research: liveResearch,
    upcoming_round: initialValues?.upcoming_round || "",
    interviewer_role: initialValues?.interviewer_role || "",
    seniority_level:
      initialValues?.seniority_level ||
      application.jobLevelNormalized ||
      application.jobLevel ||
      "",
    interview_date: initialValues?.interview_date || "",
    focus_areas:
      initialValues?.focus_areas && initialValues.focus_areas.length > 0
        ? initialValues.focus_areas
        : selectedTopics.map((topic) => topic.label || topic.topic),
    concerns: initialValues?.concerns || [],
    additional_notes: initialValues?.additional_notes || "",
  };
};

const parseFreeTextList = (value: string): string[] =>
  value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

const inferResumeSourcePreview = (application: JobApplication): string => {
  if (application.resumeSelectionType === "custom") {
    return "Tailored resume for this application";
  }
  if (application.selectedResumeId || application.resumeId) {
    return "Selected saved resume";
  }
  return "Default resume fallback";
};

const buildTopicMap = (
  topics: GuidedInterviewPrepTopicSelection[],
): Map<string, GuidedInterviewPrepTopicSelection> =>
  new Map(
    topics.map((topic) => [
      topic.topic.toLowerCase(),
      {
        ...topic,
        question_count: clampTopicCount(topic.question_count),
      },
    ]),
  );

const areStringListsEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((item, index) => item === right[index]);
};

export const InterviewPrepWizard: React.FC<InterviewPrepWizardProps> = ({
  application,
  initialValues,
  isSubmitting = false,
  onComplete,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>("topics");
  const initialRequest = useMemo(
    () => buildInitialRequest(application, initialValues),
    [application, initialValues],
  );
  const [request, setRequest] = useState<GuidedInterviewPrepRequest>(
    () => initialRequest,
  );
  const [concernsText, setConcernsText] = useState(
    () => (initialValues?.concerns || []).join("\n"),
  );
  const [tokenCosts, setTokenCosts] = useState<Record<string, number>>({});
  const [customTopicInput, setCustomTopicInput] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadTokenCosts = async () => {
      try {
        const costs = await servicePlansService.getTokenCosts();
        if (cancelled) {
          return;
        }

        const nextCosts = costs.reduce<Record<string, number>>((acc, item) => {
          acc[item.operationKey] = item.points;
          return acc;
        }, {});
        setTokenCosts(nextCosts);
      } catch (error) {
        console.error("Failed to load interview prep token costs:", error);
      }
    };

    loadTokenCosts();

    return () => {
      cancelled = true;
    };
  }, []);

  const topicContext = useMemo(
    () => ({
      technologies: application.technologies || [],
      positionSpecialization: application.positionSpecialization || [],
      responsibilities: application.responsibilities || [],
      requiredQualifications: application.requiredQualifications || [],
      optionalQualifications: application.optionalQualifications || [],
      additionalQualifications: application.additionalQualifications || [],
    }),
    [
      application.additionalQualifications,
      application.optionalQualifications,
      application.positionSpecialization,
      application.requiredQualifications,
      application.responsibilities,
      application.technologies,
    ],
  );

  const suggestedTopics = useMemo(
    () =>
      getSuggestedTopics(
        request.prep_type,
        request.job_description,
        request.position,
        topicContext,
      ),
    [request.prep_type, request.job_description, request.position, topicContext],
  );

  const topicOptions = useMemo(() => {
    const selectedTopicNames = new Set(
      (request.selected_topics || []).map((topic) => topic.topic),
    );
    const suggestedTopicNames = new Set(suggestedTopics.map((topic) => topic.value));
    const customSelections = (request.selected_topics || [])
      .filter((topic) => !suggestedTopicNames.has(topic.topic))
      .map((topic) => ({
        value: topic.topic,
        label: topic.label || topic.topic,
        description:
          topic.description || "Custom topic copied from an existing prep request.",
        defaultQuestionCount: topic.question_count,
        source: "custom" as const,
      }));

    const suggestedWithSelectionState = suggestedTopics.map((topic) => ({
      ...topic,
      selected: selectedTopicNames.has(topic.value),
    }));

    return [...suggestedWithSelectionState, ...customSelections];
  }, [request.selected_topics, suggestedTopics]);

  const selectedTopics = request.selected_topics || [];
  const selectedTopicCount = selectedTopics.length;
  const totalQuestionCount = selectedTopics.reduce(
    (sum, topic) => sum + clampTopicCount(topic.question_count),
    0,
  );
  const isEditingExistingPrep = Boolean(initialValues?.prep_id);
  const initialSelectedTopicMap = useMemo(
    () => buildTopicMap(initialRequest.selected_topics || []),
    [initialRequest.selected_topics],
  );
  const removedTopicCount = useMemo(() => {
    const selectedTopicNames = new Set(
      selectedTopics.map((topic) => topic.topic.toLowerCase()),
    );
    return Array.from(initialSelectedTopicMap.keys()).filter(
      (topicName) => !selectedTopicNames.has(topicName),
    ).length;
  }, [initialSelectedTopicMap, selectedTopics]);
  const normalizedConcerns = useMemo(
    () => parseFreeTextList(concernsText),
    [concernsText],
  );
  const contextChanged = useMemo(() => {
    if (!isEditingExistingPrep) {
      return false;
    }

    return (
      request.prep_type !== initialRequest.prep_type ||
      request.output_mode !== initialRequest.output_mode ||
      Boolean(request.live_research) !== Boolean(initialRequest.live_research) ||
      (request.upcoming_round || "") !== (initialRequest.upcoming_round || "") ||
      (request.interviewer_role || "") !== (initialRequest.interviewer_role || "") ||
      (request.seniority_level || "") !== (initialRequest.seniority_level || "") ||
      (request.interview_date || "") !== (initialRequest.interview_date || "") ||
      (request.additional_notes || "") !== (initialRequest.additional_notes || "") ||
      !areStringListsEqual(normalizedConcerns, initialRequest.concerns || [])
    );
  }, [
    initialRequest.additional_notes,
    initialRequest.concerns,
    initialRequest.interview_date,
    initialRequest.interviewer_role,
    initialRequest.live_research,
    initialRequest.output_mode,
    initialRequest.prep_type,
    initialRequest.seniority_level,
    initialRequest.upcoming_round,
    isEditingExistingPrep,
    normalizedConcerns,
    request.additional_notes,
    request.interview_date,
    request.interviewer_role,
    request.live_research,
    request.output_mode,
    request.prep_type,
    request.seniority_level,
    request.upcoming_round,
  ]);
  const changedTopicsForPricing = useMemo(() => {
    if (!isEditingExistingPrep || contextChanged) {
      return selectedTopics;
    }

    return selectedTopics.filter((topic) => {
      const baselineTopic = initialSelectedTopicMap.get(topic.topic.toLowerCase());
      if (!baselineTopic) {
        return true;
      }
      return (
        clampTopicCount(topic.question_count) !==
        clampTopicCount(baselineTopic.question_count)
      );
    });
  }, [contextChanged, initialSelectedTopicMap, isEditingExistingPrep, selectedTopics]);
  const changedTopicCount = isEditingExistingPrep
    ? (contextChanged ? selectedTopicCount : changedTopicsForPricing.length) +
      removedTopicCount
    : selectedTopicCount;
  const billableTopics = isEditingExistingPrep ? changedTopicsForPricing : selectedTopics;
  const billableQuestionCount = billableTopics.reduce(
    (sum, topic) => sum + clampTopicCount(topic.question_count),
    0,
  );
  const interviewPrepTokenCost =
    tokenCosts.interviewPrepTopicBased ?? tokenCosts.interviewPrep ?? 0;
  const liveResearchTokenCost = tokenCosts.interviewPrepLiveResearchPreview ?? 2;
  const plannerTokenCost = tokenCosts.interviewPrepPlanner ?? 0;
  const finalSynthesisTokenCost = tokenCosts.interviewPrepFinalSynthesis ?? 0;
  const selectiveRewriteTokenCost = tokenCosts.interviewPrepSelectiveRewrite ?? 0;
  const tokenPreviewReady = Object.keys(tokenCosts).length > 0;
  const includesPlanner = !isEditingExistingPrep || contextChanged;
  const includesFinalSynthesis =
    !isEditingExistingPrep || contextChanged || changedTopicCount > 0;
  const topicCostBreakdown = billableTopics.map((topic) => {
    const questionCount = clampTopicCount(topic.question_count);
    const baseCost = questionCount * interviewPrepTokenCost;
    const liveResearchCost = request.live_research ? liveResearchTokenCost : 0;
    const selectiveRewriteCost =
      questionCount >= 8 ? selectiveRewriteTokenCost : 0;

    return {
      topic: topic.label || topic.topic,
      questionCount,
      totalCost: baseCost + liveResearchCost + selectiveRewriteCost,
      baseCost,
      liveResearchCost,
      selectiveRewriteCost,
    };
  });
  const estimatedTokenCost =
    tokenPreviewReady &&
    (billableQuestionCount > 0 || includesPlanner || includesFinalSynthesis)
      ? (includesPlanner ? plannerTokenCost : 0) +
        (includesFinalSynthesis ? finalSynthesisTokenCost : 0) +
        topicCostBreakdown.reduce((sum, item) => sum + item.totalCost, 0)
      : 0;
  const liveResearchSuffix =
    request.live_research && liveResearchTokenCost > 0
      ? ` + ${liveResearchTokenCost} tokens per topic for live research`
      : request.live_research
        ? " + live research"
        : "";
  const estimatedWaitSeconds = Math.max(
    30,
    (includesPlanner ? 20 : 0) +
      (billableTopics.length > 0 ? (request.live_research ? 55 : 32) : 0) +
      Math.max(0, billableTopics.length - 1) * 4 +
      (includesFinalSynthesis ? 15 : 0),
  );
  const resumeSourcePreview = inferResumeSourcePreview(application);

  const topicPreviewSummary = useMemo(
    () =>
      selectedTopics
        .slice(0, 4)
        .map((topic) => `${topic.label || topic.topic} (${clampTopicCount(topic.question_count)})`)
        .join(", "),
    [selectedTopics],
  );

  const setTopicSelection = (option: {
    value: string;
    label: string;
    description?: string;
    defaultQuestionCount?: number;
  }) => {
    setRequest((prev) => {
      const existingTopics = prev.selected_topics || [];
      const isSelected = existingTopics.some((item) => item.topic === option.value);
      const nextTopics = isSelected
        ? existingTopics.filter((item) => item.topic !== option.value)
        : [
            ...existingTopics,
            {
              topic: option.value,
              label: option.label,
              description: option.description,
              question_count: clampTopicCount(
                option.defaultQuestionCount ?? DEFAULT_TOPIC_COUNT,
              ),
            },
          ];

      return {
        ...prev,
        selected_topics: nextTopics,
        focus_areas: nextTopics.map((topic) => topic.label || topic.topic),
      };
    });
  };

  const addCustomTopic = () => {
    const trimmedTopic = customTopicInput.trim();
    if (!trimmedTopic) {
      return;
    }
    const existingTopic = (request.selected_topics || []).some(
      (topic) => topic.topic.toLowerCase() === trimmedTopic.toLowerCase(),
    );
    if (existingTopic) {
      setCustomTopicInput("");
      return;
    }
    setRequest((prev) => {
      const nextTopics = [
        ...(prev.selected_topics || []),
        {
          topic: trimmedTopic,
          label: trimmedTopic,
          description: "Custom topic added to extend this prep.",
          question_count: DEFAULT_TOPIC_COUNT,
        },
      ];
      return {
        ...prev,
        selected_topics: nextTopics,
        focus_areas: nextTopics.map((topic) => topic.label || topic.topic),
      };
    });
    setCustomTopicInput("");
  };

  const updateTopicCount = (topicValue: string, delta: number) => {
    setRequest((prev) => {
      const nextTopics = (prev.selected_topics || [])
        .map((topic) => {
          if (topic.topic !== topicValue) {
            return topic;
          }

          return {
            ...topic,
            question_count: clampTopicCount(topic.question_count + delta),
          };
        })
        .filter((topic) => topic.question_count > 0);

      return {
        ...prev,
        selected_topics: nextTopics,
        focus_areas: nextTopics.map((topic) => topic.label || topic.topic),
      };
    });
  };

  const handlePrepTypeChange = (prepType: GuidedInterviewPrepType) => {
    setRequest((prev) => {
      const nextSuggestedTopics = getSuggestedTopics(
        prepType,
        prev.job_description,
        prev.position,
        topicContext,
      );
      const nextSuggestedTopicNames = new Set(
        nextSuggestedTopics.map((topic) => topic.value),
      );
      const currentTopicNames = new Set(
        (prev.selected_topics || []).map((topic) => topic.topic),
      );
      const nextSuggestions = nextSuggestedTopics
        .filter((topic) => currentTopicNames.has(topic.value))
        .map((topic) => ({
          topic: topic.value,
          label: topic.label,
          description: topic.description,
          question_count:
            prev.selected_topics?.find((item) => item.topic === topic.value)
              ?.question_count ?? topic.defaultQuestionCount ?? DEFAULT_TOPIC_COUNT,
        }));

      const customTopics = (prev.selected_topics || []).filter(
        (topic) => !nextSuggestedTopicNames.has(topic.topic),
      );

      const mergedTopics = [...nextSuggestions, ...customTopics];

      return {
        ...prev,
        prep_type: prepType,
        selected_topics: mergedTopics,
        focus_areas: mergedTopics.map((topic) => topic.label || topic.topic),
      };
    });
  };

  const sanitizeRequest = (): GuidedInterviewPrepRequest => {
    const normalizedTopics = (request.selected_topics || [])
      .map((topic) => normalizeTopicSelection(topic))
      .filter((topic): topic is GuidedInterviewPrepTopicSelection => Boolean(topic));

    return {
      ...request,
      selected_topics: normalizedTopics,
      focus_areas: normalizedTopics.map((topic) => topic.label || topic.topic),
      concerns: parseFreeTextList(concernsText),
      live_research: Boolean(request.live_research),
      use_live_research: Boolean(request.live_research),
    };
  };

  const canProceed = () => {
    if (currentStep === "topics") {
      return request.selected_topics && request.selected_topics.length > 0;
    }
    if (currentStep === "mode") {
      return Boolean(request.output_mode);
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === "topics") {
      setCurrentStep("mode");
      return;
    }
    if (currentStep === "mode") {
      setCurrentStep("context");
      return;
    }
    if (currentStep === "context") {
      setCurrentStep("review");
      return;
    }

    onComplete(sanitizeRequest());
  };

  const handleBack = () => {
    if (currentStep === "mode") {
      setCurrentStep("topics");
      return;
    }
    if (currentStep === "context") {
      setCurrentStep("mode");
      return;
    }
    if (currentStep === "review") {
      setCurrentStep("context");
    }
  };

  const renderTopicCards = () => (
    <div className="interview-prep-topic-grid">
      {topicOptions.map((option) => {
        const isSelected = request.selected_topics?.some(
          (topic) => topic.topic === option.value,
        );
        const selectedTopic = request.selected_topics?.find(
          (topic) => topic.topic === option.value,
        );
        const topicCount = selectedTopic?.question_count || option.defaultQuestionCount || DEFAULT_TOPIC_COUNT;

        return (
          <article
            key={option.value}
            className={`interview-prep-topic-card ${isSelected ? "selected" : ""}`}
          >
            <button
              type="button"
              className="interview-prep-topic-card-toggle"
              onClick={() => setTopicSelection(option)}
            >
              <div className="interview-prep-topic-card-copy">
                <h4>{option.label}</h4>
                <p>{option.description}</p>
              </div>
              <span className="interview-prep-topic-card-chip">
                {isSelected
                  ? "Selected"
                  : option.source === "job_description"
                    ? "JD / stack pick"
                    : "Add topic"}
              </span>
            </button>

            {isSelected && (
              <div className="interview-prep-topic-count-control">
                <span>Questions</span>
                <div className="interview-prep-topic-count-buttons">
                  <button
                    type="button"
                    onClick={() => {
                      const currentCount = selectedTopic?.question_count || topicCount;
                      if (currentCount <= 1) {
                        setTopicSelection(option);
                        return;
                      }
                      updateTopicCount(option.value, -1);
                    }}
                    aria-label={`Decrease questions for ${option.label}`}
                  >
                    −
                  </button>
                  <span>{topicCount}</span>
                  <button
                    type="button"
                    onClick={() => updateTopicCount(option.value, 1)}
                    aria-label={`Increase questions for ${option.label}`}
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );

  const renderStep = () => {
    if (currentStep === "topics") {
      return (
        <div className="interview-prep-wizard-step">
          <h3>Select Topic Areas</h3>
          <p>
            Choose the interview topics you want covered and set how many questions you
            want for each one, up to 10 per topic.
          </p>

          <div className="interview-prep-option-grid">
            {PREP_TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`interview-prep-option-card ${
                  request.prep_type === option.value ? "selected" : ""
                }`}
                onClick={() => handlePrepTypeChange(option.value as GuidedInterviewPrepType)}
              >
                <h4>{option.label}</h4>
                <p>{option.description}</p>
              </button>
            ))}
          </div>

          <div className="interview-prep-step-divider" />

          <div className="interview-prep-step-subtitle-row">
            <div>
              <h4>Topic Selection</h4>
              <p>Suggested from the job description, extracted stack, and the selected prep lens.</p>
            </div>
            <span className="interview-prep-topic-count-pill">
              {selectedTopicCount} selected
            </span>
          </div>

          <div className="interview-prep-review-card">
            <h4>Resume Source</h4>
            <p>{resumeSourcePreview}</p>
            <p className="interview-prep-review-meta">
              Interview prep evidence will use the job-specific resume first, then the selected saved resume, then the default resume.
            </p>
          </div>

          {renderTopicCards()}

          <div className="interview-prep-field">
            <label htmlFor="prep-custom-topic">Add Custom Topic</label>
            <div className="interview-prep-custom-topic-row">
              <input
                id="prep-custom-topic"
                type="text"
                value={customTopicInput}
                onChange={(event) => setCustomTopicInput(event.target.value)}
                placeholder="Add a custom topic one by one, for example: API security reviews"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addCustomTopic();
                  }
                }}
              />
              <button type="button" onClick={addCustomTopic}>
                Add
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (currentStep === "mode") {
      return (
        <div className="interview-prep-wizard-step">
          <h3>Output and Research</h3>
          <p>
            Choose the answer style and whether the generation should pull fresh live
            research for the role and company.
          </p>

          <div className="interview-prep-option-grid interview-prep-option-grid--two">
            {PREP_MODE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`interview-prep-option-card ${
                  request.output_mode === option.value ? "selected" : ""
                }`}
                onClick={() =>
                  setRequest((prev) => ({
                    ...prev,
                    output_mode: option.value as GuidedInterviewPrepMode,
                  }))
                }
              >
                <h4>{option.label}</h4>
                <p>{option.description}</p>
              </button>
            ))}
          </div>

          <div className={`interview-prep-toggle-card ${request.live_research ? "selected" : ""}`}>
            <label htmlFor="prep-live-research" className="interview-prep-toggle-card-label">
              <input
                id="prep-live-research"
                type="checkbox"
                checked={Boolean(request.live_research)}
                onChange={(event) =>
                  setRequest((prev) => ({
                    ...prev,
                    live_research: event.target.checked,
                  }))
                }
              />
              <span>
                <strong>Live research</strong>
                <small>Use live company and role research before generating the prep.</small>
              </span>
            </label>
          </div>
        </div>
      );
    }

    if (currentStep === "context") {
      return (
        <div className="interview-prep-wizard-step">
          <h3>Add Context</h3>
          <p>
            Narrow the prep so the generated questions and answers match the exact round
            and pressure points you care about.
          </p>

          <div className="interview-prep-context-grid">
            <div className="interview-prep-field">
              <label htmlFor="prep-upcoming-round">Upcoming Round</label>
              <select
                id="prep-upcoming-round"
                value={request.upcoming_round || ""}
                onChange={(event) =>
                  setRequest((prev) => ({
                    ...prev,
                    upcoming_round: event.target.value,
                  }))
                }
              >
                <option value="">Not specified</option>
                <option value="phone screen">Phone screen</option>
                <option value="first round">First round</option>
                <option value="onsite">Onsite</option>
                <option value="final round">Final round</option>
                <option value="executive round">Executive round</option>
              </select>
            </div>

            <div className="interview-prep-field">
              <label htmlFor="prep-interviewer-role">Interviewer Role</label>
              <input
                id="prep-interviewer-role"
                type="text"
                value={request.interviewer_role || ""}
                onChange={(event) =>
                  setRequest((prev) => ({
                    ...prev,
                    interviewer_role: event.target.value,
                  }))
                }
                placeholder="Hiring manager, panelist, recruiter, VP..."
              />
            </div>

            <div className="interview-prep-field">
              <label htmlFor="prep-seniority-level">Seniority Level</label>
              <input
                id="prep-seniority-level"
                type="text"
                value={request.seniority_level || ""}
                onChange={(event) =>
                  setRequest((prev) => ({
                    ...prev,
                    seniority_level: event.target.value,
                  }))
                }
                placeholder="Senior, Staff, Director, Manager..."
              />
            </div>

            <div className="interview-prep-field">
              <label htmlFor="prep-interview-date">Interview Date</label>
              <input
                id="prep-interview-date"
                type="date"
                value={request.interview_date || ""}
                onChange={(event) =>
                  setRequest((prev) => ({
                    ...prev,
                    interview_date: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="interview-prep-field">
            <label htmlFor="prep-concerns">Concerns or Gaps</label>
            <textarea
              id="prep-concerns"
              rows={4}
              value={concernsText}
              onChange={(event) => setConcernsText(event.target.value)}
              placeholder="What worries you about this interview? Missing domain experience, leadership proof, system depth..."
            />
          </div>

          <div className="interview-prep-field">
            <label htmlFor="prep-notes">Additional Notes</label>
            <textarea
              id="prep-notes"
              rows={4}
              value={request.additional_notes || ""}
              onChange={(event) =>
                setRequest((prev) => ({
                  ...prev,
                  additional_notes: event.target.value,
                }))
              }
              placeholder="Any extra context you want the prep to account for."
            />
          </div>
        </div>
      );
    }

    return (
      <div className="interview-prep-wizard-step">
        <h3>Review and Generate</h3>
        <p>
          Confirm the selected topics, generation mode, and previewed token cost before
          creating the prep request.
        </p>

        <div className="interview-prep-review-grid">
          <div className="interview-prep-review-card">
            <h4>Topics</h4>
            <p>
              {selectedTopicCount} topics, {totalQuestionCount} total questions
            </p>
            <ul className="interview-prep-review-list">
              {selectedTopics.map((topic) => (
                <li key={topic.topic}>
                  <strong>{topic.label || topic.topic}</strong>
                  <span>{clampTopicCount(topic.question_count)} questions</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="interview-prep-review-card">
            <h4>Generation Settings</h4>
            <p>{PREP_TYPE_OPTIONS.find((option) => option.value === request.prep_type)?.label}</p>
            <p>{PREP_MODE_OPTIONS.find((option) => option.value === request.output_mode)?.label}</p>
            <p>{request.live_research ? "Live research enabled" : "Live research disabled"}</p>
            <p>{resumeSourcePreview}</p>
          </div>

          <div className="interview-prep-review-card">
            <h4>Token Preview</h4>
            {tokenPreviewReady ? (
              <>
                <p className="interview-prep-review-cost">
                  {estimatedTokenCost > 0 ? `${estimatedTokenCost} tokens` : "No billable changes"}
                </p>
                <p className="interview-prep-review-meta">
                  {interviewPrepTokenCost} tokens per requested question{liveResearchSuffix}.
                </p>
                <p className="interview-prep-review-meta">
                  {includesPlanner
                    ? `${plannerTokenCost} tokens for planning`
                    : "Planning reused from the saved prep."}
                </p>
                <p className="interview-prep-review-meta">
                  {includesFinalSynthesis
                    ? `${finalSynthesisTokenCost} tokens for final synthesis`
                    : "Final synthesis reused from the saved prep."}
                </p>
                {topicCostBreakdown.map((item) => (
                  <p className="interview-prep-review-meta" key={item.topic}>
                    {item.topic.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}: {item.totalCost} tokens
                  </p>
                ))}
                {isEditingExistingPrep && (
                  <p className="interview-prep-review-meta">
                    {changedTopicCount > 0
                      ? `${changedTopicCount} topic change(s) will regenerate. Unchanged topics stay saved.`
                      : "No topic or context changes detected yet."}
                  </p>
                )}
                <p className="interview-prep-review-meta">
                  Expected wait: about {Math.max(1, Math.round(estimatedWaitSeconds / 60))} minute(s).
                </p>
              </>
            ) : (
              <p className="interview-prep-review-meta">Loading token cost preview...</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="interview-prep-wizard">
      <div className="interview-prep-wizard-steps">
        <div
          className={`interview-prep-wizard-step-pill ${
            currentStep === "topics" ? "active" : "completed"
          }`}
        >
          <span>1</span>
          <span>Topics</span>
        </div>
        <div
          className={`interview-prep-wizard-step-pill ${
            currentStep === "mode"
              ? "active"
              : currentStep === "context" || currentStep === "review"
                ? "completed"
                : ""
          }`}
        >
          <span>2</span>
          <span>Mode</span>
        </div>
        <div
          className={`interview-prep-wizard-step-pill ${
            currentStep === "context"
              ? "active"
              : currentStep === "review"
                ? "completed"
                : ""
          }`}
        >
          <span>3</span>
          <span>Context</span>
        </div>
        <div
          className={`interview-prep-wizard-step-pill ${
            currentStep === "review" ? "active" : ""
          }`}
        >
          <span>4</span>
          <span>Review</span>
        </div>
      </div>

      <div className="interview-prep-wizard-context-banner">
        <strong>{application.position || "Untitled role"}</strong>
        <span>{application.company || "Unknown company"}</span>
      </div>

      <div className="interview-prep-wizard-content">{renderStep()}</div>

      <div className="interview-prep-wizard-preview">
        <div className="interview-prep-wizard-preview-row">
          <span>Selected topics</span>
          <strong>{selectedTopicCount}</strong>
        </div>
        <div className="interview-prep-wizard-preview-row">
          <span>Total questions</span>
          <strong>{totalQuestionCount || 0}</strong>
        </div>
        <div className="interview-prep-wizard-preview-row">
          <span>Resume source</span>
          <strong>{resumeSourcePreview}</strong>
        </div>
        {isEditingExistingPrep && (
          <div className="interview-prep-wizard-preview-row">
            <span>Changed topics</span>
            <strong>{changedTopicCount}</strong>
          </div>
        )}
        <div className="interview-prep-wizard-preview-row">
          <span>Token preview</span>
          <strong>
            {tokenPreviewReady &&
            (billableQuestionCount > 0 || includesPlanner || includesFinalSynthesis)
              ? `${estimatedTokenCost} tokens`
              : isEditingExistingPrep
                ? "No billable changes"
                : "Waiting on topic selection"}
          </strong>
        </div>
        {topicPreviewSummary && (
          <div className="interview-prep-wizard-preview-note">{topicPreviewSummary}</div>
        )}
        <div className="interview-prep-wizard-preview-note">
          Expected wait: about {Math.max(1, Math.round(estimatedWaitSeconds / 60))} minute(s)
        </div>
      </div>

      <div className="interview-prep-wizard-footer">
        <button
          type="button"
          className="interview-prep-wizard-button interview-prep-wizard-button-secondary"
          onClick={currentStep === "topics" ? onCancel : handleBack}
          disabled={isSubmitting}
        >
          {currentStep === "topics" ? "Cancel" : "Back"}
        </button>
        <button
          type="button"
          className="interview-prep-wizard-button interview-prep-wizard-button-primary"
          onClick={handleNext}
          disabled={!canProceed() || isSubmitting}
        >
          {currentStep === "review"
            ? isSubmitting
              ? "Generating..."
              : isEditingExistingPrep
                ? "Update Prep"
                : "Generate Prep"
            : "Next"}
        </button>
      </div>
    </div>
  );
};
